/**
 * InstructorAIOffersAwaiting — Work Mode panel listing AI offers
 * that have finished AI grading and are awaiting the instructor's
 * 1-tap decision (Track A, Phase E).
 *
 * Mount target: Work Mode landing — visible to the instructor as
 * soon as they open /work-mode. Renders nothing when no rows are
 * awaiting.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ChevronDown, ChevronUp, Sparkles, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

import InstructorConfirmDecisionPanel from "./InstructorConfirmDecisionPanel";
import { MEDIA_FEATURE_ENABLED } from "../../lib/mediaFeatureFlag";

const API = process.env.REACT_APP_BACKEND_URL;


export default function InstructorAIOffersAwaiting() {
  const [items, setItems] = useState([]);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    if (!MEDIA_FEATURE_ENABLED) {
      setItems([]);
      return;
    }
    try {
      const r = await axios.get(
        `${API}/api/ai-offers/instructor/list?status=awaiting_instructor_confirmation`,
        { withCredentials: true },
      );
      setItems(r.data?.items || []);
    } catch {
      setItems([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (items.length === 0) return null;
  // Media quarantine — silent.
  if (!MEDIA_FEATURE_ENABLED) return null;

  return (
    <section
      data-testid="instructor-ai-offers-awaiting"
      className="rounded-2xl border border-amber-700/40 bg-gradient-to-br from-amber-900/15 to-slate-900/30 p-4 space-y-3"
    >
      <div className="flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-amber-300" />
        <h3 className="text-[10px] uppercase tracking-widest font-extrabold text-amber-300">
          AI reviews awaiting your tap
        </h3>
      </div>

      <div className="space-y-2">
        {items.map((o) => {
          const isOpen = openId === o.id;
          return (
            <div
              key={o.id}
              data-testid={`awaiting-${o.id}`}
              className="rounded-xl border border-slate-700/60 bg-slate-900/60"
            >
              <button
                type="button"
                data-testid={`awaiting-${o.id}-toggle`}
                onClick={() => setOpenId(isOpen ? null : o.id)}
                className="w-full text-left flex items-center gap-2 p-3"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-extrabold text-white truncate">
                    {o.pupil_name} · {o.review_intent_label}
                  </p>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    AI review ready — pick the next step
                  </p>
                </div>
                {isOpen
                  ? <ChevronUp className="w-4 h-4 text-slate-400" />
                  : <ChevronDown className="w-4 h-4 text-slate-400" />}
              </button>

              {isOpen && (
                <div className="px-3 pb-3 space-y-3">
                  {o.lesson_review_id && (
                    <Link
                      to={`/lesson-review?id=${o.lesson_review_id}`}
                      data-testid={`awaiting-${o.id}-open-review`}
                      className="block text-[12px] text-indigo-300 underline underline-offset-2 font-extrabold"
                    >
                      Open the AI review <ArrowRight className="inline w-3 h-3" />
                    </Link>
                  )}
                  <InstructorConfirmDecisionPanel
                    offerId={o.id}
                    reviewIntentLabel={o.review_intent_label}
                    pupilName={o.pupil_name}
                    onConfirmed={() => load()}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
